import React from 'react'
import styled from 'styled-components'
import { Formik, FormikHelpers, FormikProps, useField } from 'formik'
import * as yup from 'yup'

// Constants
import { professionalRoles, ProfessionalRole } from '@sketch/constants'
import { SKETCH_WEBSITE } from '@sketch/env-config'

// Components
import {
  Button,
  Checkbox,
  Link,
  Form,
  Input,
  CreatePasswordInput,
  passwordStrengthValidation,
  Select,
  SelectOption,
} from '@sketch/components'

// Utils
import { useAnalytics } from '@sketch/modules-common'
import { useTrackEditedFields } from './useTrackEditedFields'

// Constants
import {
  SIGN_UP_INITIAL_VALUES,
  TERMS_OF_SERVICE_PRIVACY_POLICY_ERROR,
  Values,
} from './constants'

// Hooks
import { useSignUpMetadata } from '../../hooks/useSignUpMetadata'

// Types
import {
  createPersonalAuthorization,
  setUserAuthorization,
} from 'graphql/multisession'
import {
  useSignUpMutation,
  GetInitialUserDocument,
  GetInitialUserQuery,
  SignUpMutationVariables,
} from '@sketch/gql-types'
import { ParsedError } from '@sketch/graphql-apollo/useMutation'
import { SignupBaseProps as SignupAnalyticsEventBaseProps } from '@sketch/modules-common/analytics'

const signUpSchema = yup.object().shape({
  name: yup.string().trim().required('Enter your first name'),
  professionalRole: yup.mixed().required('Select your role'),
  email: yup
    .string()
    .email('This is not a valid email')
    .required('Enter your email address'),
  password: yup
    .string()
    .min(8, 'Should be 8 or more characters')
    .max(72, 'Should be 72 or less characters')
    .required('Create a new password')
    .test(
      'passwordStrength',
      'The password strength should be at least Okay',
      passwordStrengthValidation
    ),
  'terms-privacy-policy': yup.boolean(),
  'marketing-consent': yup.boolean(),
})

export interface ExtendedFormikProps extends FormikProps<Values> {
  apiError: ParsedError | undefined
  onRoleChange: (role: ProfessionalRole) => void
  callToAction: string
  formContextForAnalytics: SignupAnalyticsEventBaseProps['formContext']
}

const StyledCheckbox = styled(Checkbox)`
  /*
  Prevent the TOS link from being "blocked" by the input.
  */
  input {
    width: auto;
  }
`

const educationRoles: ProfessionalRole[] = [
  'STUDENT',
  'TEACHER',
  'EDUCATIONAL_INSTITUTION',
]
const isEducationRole = (role: ProfessionalRole): boolean =>
  educationRoles.includes(role)

interface RoleFieldProps
  extends Pick<
    ExtendedFormikProps,
    | 'values'
    | 'errors'
    | 'touched'
    | 'handleChange'
    | 'handleBlur'
    | 'onRoleChange'
  > {}

const RoleField: React.FC<RoleFieldProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  onRoleChange,
}) => {
  const [
    professionalRoleField,
    professionalRoleMeta,
    { setValue: setRole },
  ] = useField<ProfessionalRole>({
    name: 'professionalRole',
  })

  const onChange = (value: ProfessionalRole) => {
    onRoleChange(value)
    return setRole(value)
  }

  return (
    <Form.Field
      name="professionalRole"
      label="What do you do?"
      help={
        isEducationRole(professionalRoleField.value) && (
          <>
            Use Sketch for free with an{' '}
            <Link
              variant="primary"
              isUnderlined
              external
              href={`${SKETCH_WEBSITE}/store/education`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Education Workspace ↗
            </Link>
          </>
        )
      }
      errorText={
        touched.professionalRole ? professionalRoleMeta.error : undefined
      }
    >
      <Select
        {...professionalRoleField}
        placeholder="I am a…"
        onChange={onChange}
      >
        {Object.entries(professionalRoles).map(([value, name]) => (
          <SelectOption key={value} value={value}>
            {name}
          </SelectOption>
        ))}
      </Select>
    </Form.Field>
  )
}

type FieldsProps = ExtendedFormikProps & {
  formContextForAnalytics: SignupAnalyticsEventBaseProps['formContext']
}

export const Fields: React.FC<FieldsProps> = ({
  values,
  errors,
  touched,
  handleChange,
  handleBlur,
  onRoleChange,
  formContextForAnalytics,
}) => {
  useTrackEditedFields(values, formContextForAnalytics)
  const { trackEvent } = useAnalytics()

  return (
    <>
      <RoleField
        values={values}
        errors={errors}
        touched={touched}
        handleChange={handleChange}
        handleBlur={handleBlur}
        onRoleChange={onRoleChange}
      />
      <Form.Field
        name="name"
        label="Your first name"
        errorText={touched.name ? errors.name : undefined}
      >
        <Input
          type="text"
          name="name"
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </Form.Field>
      <Form.Field
        name="email"
        label="Email"
        errorText={touched.email ? errors.email : undefined}
      >
        <Input
          name="email"
          type="email"
          value={values.email}
          onChange={handleChange}
          onBlur={handleBlur}
          autoFocus={!!values.email}
        />
      </Form.Field>
      <CreatePasswordInput
        name="password"
        value={values.password}
        onChange={handleChange}
        onBlur={handleBlur}
        invalid={touched.password && !!errors.password}
      />

      <Form.Field name="terms-privacy-policy" mt={2} mb={3}>
        <StyledCheckbox
          name="terms-privacy-policy"
          variant="untinted"
          checked={Boolean(values['terms-privacy-policy'])}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          I’ve read and agree to the{' '}
          <Link
            variant="primary"
            isUnderlined
            external
            href={`${SKETCH_WEBSITE}/tos`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('SIGN UP - "Terms of Service" link opened', {
                formContext: formContextForAnalytics,
              })
            }}
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link
            variant="primary"
            isUnderlined
            external
            href={`${SKETCH_WEBSITE}/privacy`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              trackEvent('SIGN UP - "Privacy Policy" link opened', {
                formContext: formContextForAnalytics,
              })
            }}
          >
            Privacy Policy
          </Link>
        </StyledCheckbox>
      </Form.Field>
      <Form.Field name="marketing-consent" mb={8}>
        <StyledCheckbox
          name="marketing-consent"
          variant="untinted"
          checked={Boolean(values['marketing-consent'])}
          onChange={handleChange}
          onBlur={handleBlur}
        >
          Keep me updated on news from Sketch
        </StyledCheckbox>
      </Form.Field>
    </>
  )
}

export const Errors: React.FC<ExtendedFormikProps> = ({ apiError, status }) =>
  status || apiError ? (
    <Form.ErrorField>{status || apiError?.message || ''}</Form.ErrorField>
  ) : null

export const DefaultFormFields: React.FC<ExtendedFormikProps> = formProps => {
  const { trackEvent } = useAnalytics()

  return (
    <>
      <Fields {...formProps} />
      <Errors {...formProps} />
      <Submit
        disabled={!(formProps.isValid && formProps.dirty)}
        callToAction={formProps.callToAction}
        isSubmitting={formProps.isSubmitting}
        onClick={() => {
          const submitCount = formProps.submitCount + 1
          trackEvent('SIGN UP - Sign up form submitted', {
            submitCount: submitCount,
            formContext: formProps.formContextForAnalytics,
          })
        }}
      />
    </>
  )
}
export interface SignUpFormProps {
  email?: string
  professionalRole?: SignUpMutationVariables['input']['professionalRole']
  /** User has an invitation token */
  invitedUser?: boolean
  children?: (formikProps: ExtendedFormikProps) => React.ReactNode
  onCompleted?: () => void
  onError?: (message: string) => void
  onRoleChange?: (role: ProfessionalRole) => void
  callToAction?: string
  formContextForAnalytics: SignupAnalyticsEventBaseProps['formContext']
}

export interface SubmitProps {
  isSubmitting: boolean
  callToAction?: string
  disabled?: boolean
  onClick?: () => void
}

export const Submit: React.FC<SubmitProps> = ({
  isSubmitting,
  disabled,
  onClick,
  callToAction = 'Sign Up',
}) => (
  <Form.Footer>
    <Button
      variant="primary"
      type="submit"
      fill
      size="48"
      disabled={isSubmitting || disabled}
      loading={isSubmitting}
      onClick={onClick}
    >
      {callToAction}
    </Button>
  </Form.Footer>
)

const SignUpForm: React.FC<SignUpFormProps> = ({
  email = '',
  professionalRole,
  invitedUser,
  onCompleted,
  onError,
  formContextForAnalytics,
  children = DefaultFormFields,
  onRoleChange = () => {},
  callToAction = 'Sign Up',
}) => {
  const { metadata, clearMetadata: cleanSignUpMetadata } = useSignUpMetadata()

  const [signUp, { loading, error: apiError }] = useSignUpMutation({
    redirectErrors: true,
    update: (cache, { data }) => {
      if (!data?.signUp) {
        return
      }

      /* Save the user credentials in the apollo cache */
      setUserAuthorization(
        cache,
        createPersonalAuthorization(data.signUp!.credentials!)
      )

      /* Save the user profile information */
      cache.writeQuery<GetInitialUserQuery>({
        query: GetInitialUserDocument,
        data: {
          __typename: 'RootQueryType',
          me: data.signUp.user!,
        },
      })
    },
    onCompleted: () => {
      cleanSignUpMetadata()
      onCompleted?.()
    },
    onError: ({ message }) => {
      onError?.(message)
    },
  })

  const handleFormSubmit = async (
    values: Values,
    actions: FormikHelpers<Values>
  ) => {
    try {
      if (!values['terms-privacy-policy']) {
        actions.setStatus(TERMS_OF_SERVICE_PRIVACY_POLICY_ERROR)
        return
      }

      actions.setStatus()

      const {
        'terms-privacy-policy': tosAgreed,
        'marketing-consent': marketingConsent,
        ...otherValues
      } = values

      await signUp({
        variables: {
          input: {
            ...otherValues,

            /**
             * We have unified both values on the UI
             * however BE still receives them distinctly
             *
             * https://github.com/sketch-hq/Cloud/issues/15774
             */
            privacyPolicyAgreed: tosAgreed,
            tosAgreed,

            marketingConsent,
            createWorkspace: !invitedUser,
            metadata,
          },
        },
      })
    } catch (e) {
      // The error handling should be taken care of by Apollo
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          ...SIGN_UP_INITIAL_VALUES,
          email,
          ...(professionalRole && { professionalRole }),
        }}
        onSubmit={handleFormSubmit}
        validationSchema={signUpSchema}
        validateOnBlur={false}
        enableReinitialize
      >
        {formikbag => (
          <Form>
            {children({
              ...formikbag,
              isSubmitting: loading,
              apiError,
              onRoleChange,
              callToAction,
              formContextForAnalytics,
            })}
          </Form>
        )}
      </Formik>
    </>
  )
}

export default SignUpForm
